import { useEffect, useRef } from 'react'
import { cn } from '@/lib/utils'

interface ParticleSphereAnimationProps {
  className?: string
  /** Number of particles on the sphere surface. */
  count?: number
  /** Seconds per full revolution. */
  period?: number
  /**
   * Which hemisphere to draw. Two canvases ('back' and 'front') stacked with
   * content between them make that content sit inside the globe; they share
   * the frame clock, so they turn together.
   */
  layer?: 'all' | 'back' | 'front'
}

/**
 * Rotating particle globe: points spread evenly on a sphere (Fibonacci
 * lattice), turning about a slightly tilted axis, projected with depth so
 * the near side reads larger and brighter. Colour runs from the brand blue
 * to purple across the sphere.
 *
 * Canvas, sized to its parent. Animates only while on screen and draws a
 * single static frame under reduced motion. Decorative only.
 */
export default function ParticleSphereAnimation({
  className,
  count = 900,
  period = 40,
  layer = 'all',
}: ParticleSphereAnimationProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null)

  useEffect(() => {
    const canvas = canvasRef.current
    const host = canvas?.parentElement
    const ctx = canvas?.getContext('2d')
    if (!canvas || !host || !ctx) return

    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    const golden = Math.PI * (3 - Math.sqrt(5))
    const points = Array.from({ length: count }, (_, i) => {
      const y = 1 - (i / (count - 1)) * 2
      const r = Math.sqrt(1 - y * y)
      const theta = golden * i
      return { x: Math.cos(theta) * r, y, z: Math.sin(theta) * r }
    })
    const tilt = 0.35
    const cosT = Math.cos(tilt)
    const sinT = Math.sin(tilt)

    let size = 0
    let raf = 0
    let visible = false

    const resize = () => {
      const rect = host.getBoundingClientRect()
      const dpr = Math.min(window.devicePixelRatio || 1, 2)
      size = Math.min(rect.width, rect.height)
      canvas.width = Math.round(size * dpr)
      canvas.height = Math.round(size * dpr)
      canvas.style.width = `${size}px`
      canvas.style.height = `${size}px`
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }

    const draw = (time: number) => {
      ctx.clearRect(0, 0, size, size)
      const angle = reduced ? 0 : ((time / 1000) * Math.PI * 2) / period
      const cosA = Math.cos(angle)
      const sinA = Math.sin(angle)
      const radius = size * 0.46
      const c = size / 2
      for (const p of points) {
        // Spin about Y, then tilt about X.
        const x1 = p.x * cosA + p.z * sinA
        const z1 = -p.x * sinA + p.z * cosA
        const y2 = p.y * cosT - z1 * sinT
        const z2 = p.y * sinT + z1 * cosT
        if ((layer === 'back' && z2 >= 0) || (layer === 'front' && z2 < 0)) continue
        const depth = (z2 + 1) / 2 // 0 back .. 1 front
        const sx = c + x1 * radius
        const sy = c + y2 * radius
        // Blue (#0c00ff) to purple (#b200ff) across the sphere.
        const mix = (x1 + 1) / 2
        const red = Math.round(12 + (178 - 12) * mix)
        ctx.fillStyle = `rgba(${red},0,255,${(0.12 + depth * 0.7).toFixed(3)})`
        const dot = 0.6 + depth * 1.6
        ctx.beginPath()
        ctx.arc(sx, sy, dot, 0, Math.PI * 2)
        ctx.fill()
      }
    }

    const loop = (time: number) => {
      draw(time)
      if (visible && !reduced) raf = requestAnimationFrame(loop)
    }

    resize()
    const ro = new ResizeObserver(() => {
      resize()
      if (reduced) draw(0)
    })
    ro.observe(host)
    const io = new IntersectionObserver(([entry]) => {
      visible = Boolean(entry?.isIntersecting)
      cancelAnimationFrame(raf)
      if (!visible) return
      if (reduced) draw(0)
      else raf = requestAnimationFrame(loop)
    })
    io.observe(host)

    return () => {
      cancelAnimationFrame(raf)
      ro.disconnect()
      io.disconnect()
    }
  }, [count, period, layer])

  return <canvas ref={canvasRef} aria-hidden className={cn('block h-full w-full', className)} />
}
